import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Image as ImageIcon, Loader, Bot, User } from 'lucide-react';
import { processOptimizationAPI, AIMessage } from '../../services/processOptimizationAPI';

interface AIAssistantProps {
  opportunityId: number;
  opportunityTitle: string;
  workflowBlueprintId?: number;
  onClose: () => void;
}

const AIAssistant: React.FC<AIAssistantProps> = ({
  opportunityId,
  opportunityTitle,
  workflowBlueprintId,
  onClose
}) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    initializeSession();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeSession = async () => {
    try {
      setInitializing(true);
      const session = await processOptimizationAPI.createAssistantSession(
        opportunityId,
        workflowBlueprintId
      );
      setSessionId(session.sessionId);

      // Add welcome message
      setMessages([
        {
          role: 'assistant',
          content: `Hi! I'm your AI implementation assistant. I'm here to help you implement the solution for "${opportunityTitle}".\n\nYou can:\n• Ask me specific questions about implementation\n• Upload screenshots if you're stuck\n• Request step-by-step guidance\n\nHow can I help you today?`,
          timestamp: new Date().toISOString()
        }
      ]);
    } catch (error: any) {
      console.error('Failed to initialize session:', error);
    } finally {
      setInitializing(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !sessionId || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to UI
    const newUserMessage: AIMessage = {
      role: 'user',
      content: userMessage,
      images: uploadedImages.length > 0 ? [...uploadedImages] : undefined,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newUserMessage]);
    setLoading(true);

    try {
      const response = await processOptimizationAPI.sendMessage(
        sessionId,
        userMessage,
        uploadedImages.length > 0 ? uploadedImages : undefined
      );

      // Add assistant response
      const assistantMessage: AIMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setUploadedImages([]); // Clear uploaded images after sending
    } catch (error: any) {
      const errorMessage: AIMessage = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImages(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);

    // Auto-expand textarea (ChatGPT style)
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px'; // Max 128px
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Bot className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">AI Implementation Assistant</h2>
              <p className="text-xs text-gray-600">{opportunityTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {initializing ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-2" />
                <p className="text-gray-600 text-sm">Initializing AI assistant...</p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Bot className="w-5 h-5 text-purple-600" />
                    </div>
                  )}
                  <div
                    className={`max-w-[70%] rounded-lg p-4 ${
                      message.role === 'user'
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                    }`}
                  >
                    {message.images && message.images.length > 0 && (
                      <div className="mb-2 space-y-2">
                        {message.images.map((img, imgIdx) => (
                          <img
                            key={imgIdx}
                            src={img}
                            alt="Uploaded"
                            className="max-w-full rounded-lg border border-gray-300"
                          />
                        ))}
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-line">{message.content}</div>
                    <div className={`text-xs mt-2 ${message.role === 'user' ? 'text-purple-100' : 'text-gray-500'}`}>
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Bot className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <Loader className="w-5 h-5 animate-spin text-purple-600" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Image Preview */}
        {uploadedImages.length > 0 && (
          <div className="p-2 border-t border-gray-200 bg-gray-50">
            <div className="flex gap-2 overflow-x-auto">
              {uploadedImages.map((img, idx) => (
                <div key={idx} className="relative flex-shrink-0">
                  <img src={img} alt="Preview" className="h-16 w-16 object-cover rounded-lg border border-gray-300" />
                  <button
                    onClick={() => setUploadedImages(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input - ChatGPT Style */}
        <div className="p-6 border-t border-gray-200 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-4xl mx-auto">
            {/* Main Input Container */}
            <div className="relative flex items-end gap-2 bg-white rounded-full shadow-lg border border-gray-200 px-2 py-2 hover:shadow-xl transition-all duration-200">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageUpload}
              />

              {/* Image Upload Button - Left Circle */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200 hover:scale-110"
                title="Upload screenshot"
                disabled={loading || initializing}
              >
                <ImageIcon className="w-5 h-5 text-gray-600" />
              </button>

              {/* Textarea */}
              <textarea
                value={inputMessage}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about implementing this solution..."
                className="flex-1 px-4 py-3 bg-transparent focus:outline-none resize-none max-h-32 text-gray-900 placeholder-gray-400"
                rows={1}
                disabled={loading || initializing}
                style={{ minHeight: '24px' }}
              />

              {/* Send Button - Right Circle */}
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || loading || initializing}
                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 ${
                  !inputMessage.trim() || loading || initializing
                    ? 'bg-gray-200 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md'
                }`}
                title="Send message"
              >
                <Send className={`w-5 h-5 ${
                  !inputMessage.trim() || loading || initializing ? 'text-gray-400' : 'text-white'
                }`} />
              </button>
            </div>

            {/* Tip Text */}
            <p className="text-xs text-gray-500 mt-3 text-center">
              💡 Tip: Upload screenshots if you're stuck, and I'll help you troubleshoot!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
